import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAuditLogs, exportAuditLogsCsv, exportAuditLogsJson, AuditLogFilters } from '../services/api';
import { useTimezone } from '../context/TimezoneContext';
import { AuditLog } from '../types';

const EVENT_TYPES = [
  { value: '', label: 'All types' },
  { value: 'user_action', label: 'User actions' },
  { value: 'device_action', label: 'Device actions' },
  { value: 'alert_action', label: 'Alert actions' },
  { value: 'tenant_action', label: 'Tenant actions' },
  { value: 'mqtt_action', label: 'MQTT actions' },
  { value: 'system_action', label: 'System events' },
];

const SEVERITIES = [
  { value: '', label: 'All severities' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
  { value: 'critical', label: 'Critical' },
];

export default function Audit() {
  const { formatTime } = useTimezone();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const now = new Date();
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

  const [start, setStart] = useState(toDateTimeLocal(sevenDaysAgo));
  const [end, setEnd] = useState(toDateTimeLocal(now));
  const [eventType, setEventType] = useState('');
  const [severity, setSeverity] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showSystemEvents, setShowSystemEvents] = useState(false);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);

  const filters = useMemo<AuditLogFilters>(() => {
    const params: AuditLogFilters = { limit, offset };
    if (start) params.start = new Date(start).toISOString();
    if (end) params.end = new Date(end).toISOString();
    if (eventType) {
      params.event_type = eventType;
    } else if (!showSystemEvents) {
      params.event_type = 'user_action,device_action,alert_action,tenant_action,mqtt_action';
    }
    if (severity) params.severity = severity;
    if (actionFilter) params.action = actionFilter;
    if (search) params.search = search;
    return params;
  }, [start, end, eventType, showSystemEvents, severity, actionFilter, search, limit, offset]);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAuditLogs(filters);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load audit logs.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  useEffect(() => {
    const interval = setInterval(loadLogs, 30000);
    return () => clearInterval(interval);
  }, [loadLogs]);

  const severityCounts = useMemo(() => {
    return logs.reduce(
      (acc, log) => {
        acc[log.severity] = (acc[log.severity] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );
  }, [logs]);

  const uniqueActors = useMemo(
    () => new Set(logs.map((log) => log.user_email).filter(Boolean)).size,
    [logs]
  );
  const latestEvent = logs[0];
  const pageStart = logs.length > 0 ? offset + 1 : 0;
  const pageEnd = offset + logs.length;

  function applyFilters() {
    setOffset(0);
    loadLogs();
  }

  function exportCsv() {
    exportAuditLogsCsv(filters).catch(() => setError('CSV export failed.'));
  }

  function exportJson() {
    exportAuditLogsJson(filters).catch(() => setError('JSON export failed.'));
  }

  function toggleExpanded(id: string) {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  return (
    <section className="sd-page-stack sd-audit-page">
      <div className="sd-page-header">
        <div className="sd-heading">
          <div className="sd-eyebrow">GOVERNANCE</div>
          <h1>Audit Log</h1>
          <p>Track user actions, device changes, MQTT activity, alerts, and system events.</p>
        </div>
        <div className="sd-page-actions">
          <button className="sd-secondary-button" onClick={exportCsv} type="button">Export CSV</button>
          <button className="sd-secondary-button" onClick={exportJson} type="button">Export JSON</button>
          <button className="sd-primary-button" onClick={applyFilters} type="button">
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <section className="sd-kpi-grid" aria-label="Audit metrics">
        <article className="sd-kpi-card">
          <div className="sd-kpi-label">EVENTS</div>
          <div className="sd-kpi-value">{total}</div>
          <div className="sd-kpi-detail">{logs.length} loaded on this page</div>
          <div className="sd-kpi-footer"><span>{showSystemEvents ? 'System events included' : 'System events hidden'}</span></div>
        </article>
        <article className="sd-kpi-card sd-kpi-danger">
          <div className="sd-kpi-label">HIGH SEVERITY</div>
          <div className="sd-kpi-value">{(severityCounts.error || 0) + (severityCounts.critical || 0)}</div>
          <div className="sd-kpi-detail">{severityCounts.critical || 0} critical</div>
          <div className="sd-kpi-footer"><span>Visible page sample</span></div>
        </article>
        <article className="sd-kpi-card sd-kpi-warning">
          <div className="sd-kpi-label">WARNINGS</div>
          <div className="sd-kpi-value">{severityCounts.warning || 0}</div>
          <div className="sd-kpi-detail">Needs review if rising</div>
          <div className="sd-kpi-footer"><span>Visible page sample</span></div>
        </article>
        <article className="sd-kpi-card sd-kpi-success">
          <div className="sd-kpi-label">ACTORS</div>
          <div className="sd-kpi-value">{uniqueActors}</div>
          <div className="sd-kpi-detail">{latestEvent ? `Latest: ${latestEvent.action}` : 'No recent activity'}</div>
          <div className="sd-kpi-footer"><span>{latestEvent ? formatTime(latestEvent.created_at) : 'Awaiting log events'}</span></div>
        </article>
      </section>

      {error && <div className="sd-inline-alert is-danger">{error}</div>}

      <article className="sd-panel sd-audit-filter-panel">
        <div className="sd-panel-header">
          <div>
            <h2>Investigation Filters</h2>
            <p>Narrow the log stream by time, severity, event class, actor text, and action name.</p>
          </div>
          <span className={`sd-status-label ${loading ? 'is-maintenance' : 'is-online'}`}>
            <i />
            {loading ? 'loading' : 'synced'}
          </span>
        </div>
        <div className="sd-audit-filter-grid">
          <label className="sd-field">
            <span>Start</span>
            <input type="datetime-local" value={start} onChange={(event) => setStart(event.target.value)} />
            <small>Only show events created after this time.</small>
          </label>
          <label className="sd-field">
            <span>End</span>
            <input type="datetime-local" value={end} onChange={(event) => setEnd(event.target.value)} />
            <small>Only show events created before this time.</small>
          </label>
          <label className="sd-field">
            <span>Type</span>
            <select value={eventType} onChange={(event) => setEventType(event.target.value)}>
              {EVENT_TYPES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <small>Filter by audit event family, such as device, alert, or auth activity.</small>
          </label>
          <label className="sd-field">
            <span>Severity</span>
            <select value={severity} onChange={(event) => setSeverity(event.target.value)}>
              {SEVERITIES.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <small>Use severity to focus on info, warnings, errors, or critical events.</small>
          </label>
          <label className="sd-field">
            <span>Action</span>
            <input
              placeholder="e.g. device_created"
              value={actionFilter}
              onChange={(event) => setActionFilter(event.target.value)}
            />
            <small>Exact or partial action name to investigate, such as device_updated.</small>
          </label>
          <label className="sd-field sd-audit-search-field">
            <span>Search</span>
            <input
              placeholder="e.g. user email, device name, message text..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            <small>Free-text search across message, actor, device, and metadata.</small>
          </label>
          <label className="sd-field">
            <span>Limit</span>
            <select value={limit} onChange={(event) => setLimit(Number(event.target.value))}>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
            <small>Maximum number of events to request per page.</small>
          </label>
          <label className="sd-checkbox-card sd-audit-system-toggle">
            <input
              checked={showSystemEvents}
              onChange={(event) => {
                setShowSystemEvents(event.target.checked);
                setOffset(0);
              }}
              type="checkbox"
            />
            <span>
              <strong>System Events</strong>
              <small>Include high-volume parser and broker events.</small>
            </span>
          </label>
        </div>
      </article>

      <article className="sd-panel sd-audit-log-panel">
        <div className="sd-panel-header">
          <div>
            <h2>Event Stream</h2>
            <p>Showing {pageStart}-{pageEnd} of {total} events.</p>
          </div>
          <div className="sd-row-actions">
            <button
              disabled={offset === 0}
              onClick={() => setOffset((current) => Math.max(0, current - limit))}
              type="button"
            >
              Previous
            </button>
            <button
              disabled={offset + logs.length >= total}
              onClick={() => setOffset((current) => current + limit)}
              type="button"
            >
              Next
            </button>
          </div>
        </div>
        <div className="sd-table-wrap sd-audit-table-wrap">
          <table className="sd-table sd-audit-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Severity</th>
                <th>Type</th>
                <th>Action</th>
                <th>User</th>
                <th>Device</th>
                <th>Message</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {loading && logs.length === 0 ? (
                <tr><td colSpan={8} className="sd-empty-row">Loading audit events...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={8} className="sd-empty-row">No audit events found.</td></tr>
              ) : (
                logs.map((log) => (
                  <React.Fragment key={log.id}>
                    <tr className="sd-audit-row">
                      <td className="sd-mono">{formatTime(log.created_at)}</td>
                      <td>
                        <span className={`sd-audit-severity is-${log.severity}`}>
                          <i />
                          {log.severity}
                        </span>
                      </td>
                      <td><code>{log.event_type}</code></td>
                      <td><strong>{log.action}</strong></td>
                      <td>{log.user_email || '-'}</td>
                      <td>{log.device_name || '-'}</td>
                      <td>
                        <div className="sd-audit-message-cell">
                          <span>{log.message}</span>
                          {log.metadata && Object.keys(log.metadata).length > 0 && (
                            <button onClick={() => toggleExpanded(log.id)} type="button">
                              {expanded[log.id] ? 'Hide metadata' : 'View metadata'}
                            </button>
                          )}
                        </div>
                      </td>
                      <td>{log.ip_address || '-'}</td>
                    </tr>
                    {expanded[log.id] && log.metadata && (
                      <tr className="sd-audit-metadata-row">
                        <td colSpan={8}>
                          <pre>{JSON.stringify(log.metadata, null, 2)}</pre>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="sd-audit-card-list">
          {loading && logs.length === 0 ? (
            <div className="sd-mobile-empty-card">Loading audit events...</div>
          ) : logs.length === 0 ? (
            <div className="sd-mobile-empty-card">No audit events found.</div>
          ) : (
            logs.map((log) => (
              <article className="sd-audit-card" key={log.id}>
                <header>
                  <span className={`sd-audit-severity is-${log.severity}`}>
                    <i />
                    {log.severity}
                  </span>
                  <time className="sd-mono">{formatTime(log.created_at)}</time>
                </header>
                <h3>{log.action}</h3>
                <p>{log.message}</p>
                <div className="sd-audit-card-grid">
                  <div className="sd-audit-card-fact">
                    <span>Type</span>
                    <code>{log.event_type}</code>
                  </div>
                  <div className="sd-audit-card-fact">
                    <span>User</span>
                    <strong>{log.user_email || '-'}</strong>
                  </div>
                  <div className="sd-audit-card-fact">
                    <span>Device</span>
                    <strong>{log.device_name || '-'}</strong>
                  </div>
                  <div className="sd-audit-card-fact">
                    <span>IP</span>
                    <strong>{log.ip_address || '-'}</strong>
                  </div>
                </div>
                {log.metadata && Object.keys(log.metadata).length > 0 && (
                  <button onClick={() => toggleExpanded(log.id)} type="button">
                    {expanded[log.id] ? 'Hide metadata' : 'View metadata'}
                  </button>
                )}
                {expanded[log.id] && log.metadata && (
                  <pre>{JSON.stringify(log.metadata, null, 2)}</pre>
                )}
              </article>
            ))
          )}
        </div>
        <div className="sd-audit-pagination">
          <button
            className="sd-secondary-button"
            disabled={offset === 0}
            onClick={() => setOffset((current) => Math.max(0, current - limit))}
            type="button"
          >
            Previous
          </button>
          <span>Showing {pageStart}-{pageEnd} of {total}</span>
          <button
            className="sd-secondary-button"
            disabled={offset + logs.length >= total}
            onClick={() => setOffset((current) => current + limit)}
            type="button"
          >
            Next
          </button>
        </div>
      </article>
    </section>
  );
}

function toDateTimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hour = pad(date.getHours());
  const minute = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hour}:${minute}`;
}
